/**
 * A count of the dismissible overlays currently open inside Deckorate.
 *
 * DeckorateModule's Escape/Backspace ladder navigates back to the home grid,
 * and its keydown listener is capture-phase on `document` — so it sees the
 * key *before* every modal's own bubble-phase Escape-to-close handler. Left
 * unguarded, Escape inside the Replace Colours modal would tear down the
 * whole panel instead of closing the modal the user was looking at.
 *
 * Two sources, because neither covers everything on its own:
 *
 * - The counter below, for overlays built from plain elements — the ones
 *   `useModalDismiss` drives (Replace Colours, Non-Brand, Convert to
 *   Infographic), which a DOM query has no reliable way to recognise.
 * - A live `dialog[open]` query, for the overlays that *are* native dialogs
 *   (Asset Library, the command palette, Grid Config, Apply Brand's report).
 *   Querying beats registering them: `showModal()` sets the attribute
 *   itself, so there is no React state to hook a registration onto.
 */

let openLayers = 0;

/**
 * Marks an overlay as open, returning its release function. Call from an
 * effect's setup and return the releaser as its cleanup, so an unmount can
 * never strand the count above zero. Releasing twice is a no-op — an effect
 * cleanup that runs again (StrictMode) must not push the count negative.
 */
export function registerDismissLayer(): () => void {
  openLayers += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    openLayers -= 1;
  };
}

/** True while anything is open that should take Escape before the pane's
 * back-to-home gesture does. */
export function hasOpenDismissLayer(): boolean {
  return openLayers > 0 || document.querySelector("dialog[open]") !== null;
}
